/* eslint-disable no-console */
import { createIdpToken$, getIdpToken, idpToken$ } from '@lseg-ciam/auth/token';
import { decodeJwtPayload, flog } from '@lseg-ciam/auth/utils';
import { html, LitElement } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { Subscription } from 'rxjs';
import { rigSharedStyles } from './heimdall-rig-styles.js';

@customElement('heimdall-rig-idp-token')
export class HeimdallRigIdpToken extends LitElement {
  @state()
  _subscription: Subscription | null = null;

  @state()
  _isSubscribed = false;

  @state()
  _lane: string | undefined;

  @state()
  _tokenDisplay = '×';

  @state()
  _promiseResult = '';

  @state()
  _decodedToken = '';

  onLaneChanged(event: Event) {
    const target = event.target as HTMLInputElement;
    this._lane = target.value || undefined;
    this.clearSubscription();
  }

  onSetLane(lane?: string) {
    this._lane = lane;
    this.clearSubscription();
  }

  onSubscriptionRequestChanged(event: Event) {
    const target = event.target as HTMLInputElement;
    const { checked } = target;

    if (!checked) {
      this._tokenDisplay = '×';
      this._isSubscribed = false;
      this._decodedToken = '';
      return this._subscription?.unsubscribe();
    }

    this._isSubscribed = true;
    this._subscription = new Subscription();

    const idpTokenStream$ = this._lane
      ? createIdpToken$(this._lane)
      : idpToken$;

    const laneLabel = this._lane ? ` [${this._lane}]` : '';

    this._subscription.add(
      idpTokenStream$
        .pipe(
          flog(
            `IDP token on RIG${laneLabel}`,
            'background: lightblue; color: navy; padding: 5px;'
          )
        )
        .subscribe((token) => {
          if (!token) {
            this._tokenDisplay = 'null';
            this._decodedToken = '';
            return;
          }
          this._tokenDisplay = `access: ${token.access_token?.substring(
            0,
            20
          )}...`;
          try {
            const decoded = {
              access_token: token.access_token
                ? decodeJwtPayload(token.access_token)
                : null,
              id_token: token.id_token
                ? decodeJwtPayload(token.id_token)
                : null,
            };
            this._decodedToken = JSON.stringify(decoded, null, 2);
          } catch (e) {
            this._decodedToken = `Failed to decode: ${String(e)}`;
          }
        })
    );
  }

  async onGetToken() {
    this._promiseResult = 'Loading...';
    try {
      const token = await getIdpToken(this._lane);
      if (!token) {
        this._promiseResult = 'null';
        return;
      }
      this._promiseResult = `✓ access: ${token.access_token?.substring(
        0,
        20
      )}...`;
      console.log(
        `%cIDP Token (getIdpToken)${this._lane ? ` [${this._lane}]` : ''}`,
        'background: lightgreen; color: navy; padding: 5px;',
        token
      );
    } catch (e) {
      this._promiseResult = `Error: ${String(e)}`;
      console.error('getIdpToken failed:', e);
    }
  }

  clearSubscription() {
    if (this._subscription) {
      this._subscription.unsubscribe();
      this._subscription = null;
      this._tokenDisplay = '×';
      this._isSubscribed = false;
      this._decodedToken = '';
    }
    this._promiseResult = '';
  }

  render() {
    return html`
      <div class="app">
        <h1>IDP Token Rig${this._lane ? ` (${this._lane})` : ''}</h1>
        <h2>Stream: ${this._tokenDisplay}</h2>
        <h3>Promise: ${this._promiseResult}</h3>
        ${this._decodedToken
          ? html`<pre>${this._decodedToken}</pre>`
          : html`<p><em>No decoded token yet</em></p>`}
        <ul>
          <li>
            <label>Lane</label>
            <input
              type="text"
              @change=${this.onLaneChanged}
              .value=${this._lane || ''}
              placeholder="lane or empty for 'base'"
            />
            <button @click=${() => this.onSetLane('AAAA')}>AAAA</button>
            <button @click=${() => this.onSetLane('BBBB')}>BBBB</button>
            <button @click=${() => this.onSetLane(undefined)}>Clear</button>
          </li>
          <li>
            <label>
              <input
                type="checkbox"
                @change=${this.onSubscriptionRequestChanged}
                .checked=${this._isSubscribed}
              />
              Subscribe to IDP token stream
            </label>
          </li>
          <li>
            <button @click=${this.onGetToken}>Get IDP Token (promise)</button>
          </li>
        </ul>
      </div>
    `;
  }

  static styles = rigSharedStyles;
}

declare global {
  interface HTMLElementTagNameMap {
    'heimdall-rig-idp-token': HeimdallRigIdpToken;
  }
}
